import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  CircleDashed,
  Clock3,
  MapPin,
  Search,
  Sparkles,
  WalletCards,
  TrendingUp,
  Building2,
} from "lucide-react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:5000";
const ENDPOINT = "/api/projects";

const fallbackProjects = [
  {
    id: 1,
    title: "Sample TETFund Intervention Project",
    slug: "sample-tetfund-intervention-project",
    funding_source: "TETFund",
    category: "Infrastructure",
    status: "ongoing",
    progress: 65,
    location: "Main Campus",
    summary:
      "Sample institutional project record. Replace with live CMS content by creating real TETFund projects from the admin dashboard.",
    budget: "₦450M",
    start_date: "2026-01-15",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0 },
};

function titleOf(item) {
  return item.title || item.name || "Institutional Project";
}

function textOf(item) {
  return item.summary || item.description || "Project record and progress update.";
}

function locationOf(item) {
  return item.location || item.site || item.campus || "Federal Polytechnic Bida";
}

function statusOf(item) {
  return String(item.status || "planned").toLowerCase();
}

function sourceOf(item) {
  return item.funding_source || item.source || item.category || "Institutional";
}

function progressOf(item) {
  return Math.max(
    0,
    Math.min(
      100,
      Number(item.progress || item.progress_percentage || item.completion_percentage || 0)
    )
  );
}

function budgetOf(item) {
  return item.budget || item.project_cost || item.amount || "Budget not stated";
}

function dateOf(item) {
  const value = item.start_date || item.created_at || item.updated_at;
  if (!value) return "In progress";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

function detailHref(item) {
  return `/tetfund-projects/${item.slug || item.id || ""}`;
}

function statusMeta(status) {
  const s = String(status).toLowerCase();
  if (s === "completed") return { label: "Completed", icon: CheckCircle2, tone: "bg-emerald-100 text-emerald-700" };
  if (s === "ongoing") return { label: "Ongoing", icon: TrendingUp, tone: "bg-blue-100 text-blue-700" };
  if (s === "suspended") return { label: "Suspended", icon: CircleDashed, tone: "bg-rose-100 text-rose-700" };
  return { label: "Planned", icon: Clock3, tone: "bg-amber-100 text-amber-700" };
}

export default function TetfundProjectsEnhanced() {
  const [projects, setProjects] = useState([]);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  useEffect(() => {
    let mounted = true;
    fetch(`${API_BASE}${ENDPOINT}`)
      .then((res) => res.json())
      .then((data) => {
        if (!mounted) return;
        setProjects(Array.isArray(data) && data.length ? data : fallbackProjects);
      })
      .catch(() => {
        if (mounted) setProjects(fallbackProjects);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const filtered = useMemo(() => {
    return projects.filter((item) => {
      const matchesStatus =
        statusFilter === "all" || statusOf(item) === statusFilter;
      const haystack = `${titleOf(item)} ${textOf(item)} ${locationOf(item)} ${sourceOf(item)}`.toLowerCase();
      const matchesQuery = haystack.includes(query.toLowerCase());
      return matchesStatus && matchesQuery;
    });
  }, [projects, query, statusFilter]);

  const count = (status) =>
    projects.filter((item) => statusOf(item) === status).length;

  const featured = filtered[0] || projects[0] || fallbackProjects[0];

  return (
    <main className="min-h-screen bg-slate-50">
      <section className="relative overflow-hidden bg-slate-950 text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(59,130,246,.24),transparent_32%),radial-gradient(circle_at_85%_15%,rgba(16,185,129,.22),transparent_35%),linear-gradient(135deg,#04101e,#06253a_45%,#054a3b)]" />
        <div className="absolute inset-0 opacity-[0.08]" style={{ backgroundImage: "linear-gradient(rgba(255,255,255,.7) 1px, transparent 1px),linear-gradient(90deg, rgba(255,255,255,.7) 1px, transparent 1px)", backgroundSize: "44px 44px" }} />
        <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
          <motion.div variants={fadeUp} initial="hidden" animate="show">
            <p className="mb-4 flex items-center gap-2 text-xs font-black uppercase tracking-[0.38em] text-amber-300">
              <Sparkles className="h-4 w-4" /> Project Tracker
            </p>
            <div className="grid gap-10 lg:grid-cols-[1.25fr_.75fr] lg:items-end">
              <div>
                <h1 className="max-w-4xl text-5xl font-black leading-[0.95] tracking-tight md:text-6xl">
                  Transparent institutional projects and interventions.
                </h1>
                <p className="mt-6 max-w-2xl text-lg leading-8 text-white/80">
                  Track TETFund, infrastructure, ICT, research and institutional development projects with progress updates, location details and delivery status.
                </p>
              </div>

              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.15 }}
                className="rounded-3xl border border-white/10 bg-white/10 p-6 shadow-2xl backdrop-blur-lg"
              >
                <p className="text-xs font-black uppercase tracking-[0.25em] text-white/65">
                  Featured project
                </p>
                <h2 className="mt-3 text-2xl font-black leading-tight">
                  {titleOf(featured)}
                </h2>
                <div className="mt-4 flex flex-wrap gap-2">
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white/80">
                    {sourceOf(featured)}
                  </span>
                  <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-bold text-white/80">
                    {locationOf(featured)}
                  </span>
                </div>
                <p className="mt-4 text-sm leading-7 text-white/78">
                  {textOf(featured)}
                </p>
                <div className="mt-5">
                  <div className="mb-2 flex items-center justify-between text-sm font-bold">
                    <span>Completion</span>
                    <span>{progressOf(featured)}%</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-white/10">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${progressOf(featured)}%` }}
                      transition={{ duration: 0.9, delay: 0.2 }}
                      className="h-full rounded-full bg-gradient-to-r from-emerald-400 to-cyan-300"
                    />
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {[
                ["Total", projects.length],
                ["Planned", count("planned")],
                ["Ongoing", count("ongoing")],
                ["Completed", count("completed")],
                ["Suspended", count("suspended")],
              ].map(([label, value], index) => (
                <motion.div
                  key={label}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.06 * index }}
                  className="rounded-2xl border border-white/10 bg-white/10 p-5 shadow-xl backdrop-blur"
                >
                  <p className="text-xs font-black uppercase tracking-[0.22em] text-white/65">
                    {label}
                  </p>
                  <div className="mt-2 text-3xl font-black">{value}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-6 py-12 lg:px-8">
        <div className="mb-8 grid gap-4 rounded-3xl border border-slate-200 bg-white p-5 shadow-sm lg:grid-cols-[1fr_auto] lg:items-center">
          <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
            <Search className="h-5 w-5 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search projects, campus, funding type..."
              className="w-full bg-transparent text-sm font-semibold text-slate-700 outline-none placeholder:text-slate-400"
            />
          </div>

          <div className="flex flex-wrap gap-3">
            {["all", "planned", "ongoing", "completed", "suspended"].map((item) => (
              <button
                key={item}
                onClick={() => setStatusFilter(item)}
                className={`rounded-full px-4 py-2 text-sm font-black capitalize transition ${
                  statusFilter === item
                    ? "bg-slate-950 text-white shadow-lg"
                    : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>
        </div>

        <div className="mb-8 grid gap-6 lg:grid-cols-[1fr_320px]">
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((item, index) => {
              const meta = statusMeta(statusOf(item));
              const StatusIcon = meta.icon;
              return (
                <motion.article
                  key={item.id || item.slug || index}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="show"
                  viewport={{ once: true, amount: 0.15 }}
                  transition={{ delay: index * 0.04 }}
                  whileHover={{ y: -8 }}
                  className="group overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm transition hover:shadow-2xl hover:shadow-slate-200/70"
                >
                  <div className="relative h-52 overflow-hidden bg-gradient-to-br from-slate-900 via-sky-900 to-emerald-900">
                    <motion.div
                      animate={{ scale: [1, 1.04, 1] }}
                      transition={{ duration: 8, repeat: Infinity }}
                      className="absolute inset-0 bg-[radial-gradient(circle_at_18%_18%,rgba(255,255,255,.2),transparent_20%),radial-gradient(circle_at_82%_22%,rgba(255,255,255,.14),transparent_18%)]"
                    />
                    <div className="absolute inset-0 flex flex-col justify-between p-5 text-white">
                      <div className="flex items-center justify-between">
                        <span className="rounded-full bg-white/10 px-3 py-1 text-xs font-black uppercase tracking-wide">
                          {sourceOf(item)}
                        </span>
                        <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-black ${meta.tone}`}>
                          <StatusIcon className="h-3.5 w-3.5" /> {meta.label}
                        </span>
                      </div>
                      <div>
                        <p className="mb-1 text-sm font-semibold text-white/80">
                          {item.category || "Institutional Project"}
                        </p>
                        <h3 className="max-w-[16rem] text-2xl font-black leading-tight">
                          {titleOf(item)}
                        </h3>
                      </div>
                    </div>
                  </div>

                  <div className="p-5">
                    <div className="mb-4 flex flex-wrap items-center gap-4 text-sm text-slate-500">
                      <span className="inline-flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 text-emerald-600" />
                        {locationOf(item)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <WalletCards className="h-4 w-4 text-blue-600" />
                        {budgetOf(item)}
                      </span>
                    </div>

                    <div className="mb-3 flex items-center justify-between text-sm font-bold text-slate-700">
                      <span>Progress</span>
                      <span>{progressOf(item)}%</span>
                    </div>
                    <div className="mb-4 h-2 overflow-hidden rounded-full bg-slate-100">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: `${progressOf(item)}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1 }}
                        className="h-full rounded-full bg-gradient-to-r from-blue-600 to-emerald-500"
                      />
                    </div>

                    <p className="line-clamp-3 text-sm leading-7 text-slate-600">
                      {textOf(item)}
                    </p>

                    <div className="mt-5 flex items-center justify-between">
                      <span className="text-xs font-bold uppercase tracking-wide text-slate-400">
                        Started {dateOf(item)}
                      </span>
                      <Link
                        to={detailHref(item)}
                        className="inline-flex items-center gap-2 rounded-full bg-slate-950 px-4 py-2 text-sm font-black text-white transition hover:bg-emerald-700"
                      >
                        View details <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              );
            })}
          </div>

          <aside className="space-y-6">
            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm"
            >
              <p className="text-xs font-black uppercase tracking-[0.28em] text-emerald-700">
                Delivery Insights
              </p>
              <h3 className="mt-3 text-2xl font-black text-slate-900">
                Project health summary
              </h3>
              <div className="mt-6 space-y-4">
                {[
                  ["Completion average", `${Math.round(projects.reduce((a, b) => a + progressOf(b), 0) / (projects.length || 1))}%`],
                  ["Active sites", `${count("ongoing") + count("planned")}`],
                  ["Funding streams", `${new Set(projects.map(sourceOf)).size || 1}`],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-2xl bg-slate-50 p-4">
                    <div className="text-xs font-black uppercase tracking-wide text-slate-400">
                      {label}
                    </div>
                    <div className="mt-2 text-2xl font-black text-slate-900">{value}</div>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="rounded-[28px] border border-slate-200 bg-gradient-to-br from-blue-700 to-violet-700 p-6 text-white shadow-xl"
            >
              <Building2 className="h-8 w-8 text-white/85" />
              <h3 className="mt-4 text-2xl font-black">Institutional transparency</h3>
              <p className="mt-3 text-sm leading-7 text-white/85">
                This page is designed to present live progress snapshots in a clean, public-facing format that is easier to scan, understand and trust.
              </p>
            </motion.div>
          </aside>
        </div>
      </section>
    </main>
  );
}
